import { api } from '../../lib/api';
import { getAuthToken } from '../../lib/auth';
import { defaultSettings, SettingsModel } from './schema';

export async function disableAccount(token: string) {
  return api.postAuth('/users/deactivate', {}, token);
}

export async function deleteAccount(token: string) {
  return api.deleteAuth('/users/me', {}, token);
}

// ---- Client-side settings stub ----
// Several settings sections write via patchMySettingsSection().
// Backend endpoints are not implemented yet, so persist locally
// to keep the UI functional and the build happy.

type SectionId = 'account' | 'profile' | 'appearance' | 'notifications' | 'privacy' | 'devices' | 'integrations' | 'advanced';

function readLocalSettings(): Record<string, any> {
  try {
    const raw = localStorage.getItem('settings');
    if (!raw) return {};
    const obj = JSON.parse(raw);
    return obj && typeof obj === 'object' ? obj : {};
  } catch {
    return {};
  }
}

function writeLocalSettings(obj: Record<string, any>) {
  try { localStorage.setItem('settings', JSON.stringify(obj)); } catch {}
}

export async function patchMySettingsSection(section: SectionId, patch: Record<string, any>): Promise<void> {
  const token = getAuthToken();
  if (token) {
    const current = readLocalSettings();
    const cur = current[section] && typeof current[section] === 'object' ? current[section] : {};
    const nextSection = { ...cur, ...patch };
    try {
      const res = await api.patchAuth('/users/me/settings', { settings: { [section]: nextSection } }, token);
      if (res?.settings) writeLocalSettings(res.settings);
      return;
    } catch {
      // Fall through to local cache so settings remain usable offline.
    }
  }
  try {
    const all = readLocalSettings();
    const cur = all[section] && typeof all[section] === 'object' ? all[section] : {};
    all[section] = { ...cur, ...patch };
    writeLocalSettings(all);
  } catch {
    // ignore — best-effort local persistence
  }
}

export async function fetchMySettings(): Promise<SettingsModel> {
  const token = getAuthToken();
  if (token) {
    try {
      const res = await api.getAuth('/users/me/settings', token);
      if (res?.settings) {
        writeLocalSettings(res.settings);
        return mergeWithDefaults(res.settings);
      }
    } catch {
      // Fall back to local cache.
    }
  }
  return mergeWithDefaults(readLocalSettings());
}

function mergeWithDefaults(local: Record<string, any>): SettingsModel {
  // Merge any locally saved overrides on top of defaults
  const merged: SettingsModel = {
    ...defaultSettings,
    account: { ...defaultSettings.account, ...(local.account || {}) },
    profile: { ...defaultSettings.profile, ...(local.profile || {}) },
    appearance: { ...defaultSettings.appearance, ...(local.appearance || {}) },
    notifications: { ...defaultSettings.notifications, ...(local.notifications || {}) },
    privacy: { ...defaultSettings.privacy, ...(local.privacy || {}) },
    devices: { ...defaultSettings.devices, ...(local.devices || {}) },
    integrations: { ...defaultSettings.integrations, ...(local.integrations || {}) },
    advanced: { ...defaultSettings.advanced, ...(local.advanced || {}) },
  };
  return merged;
}
